/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ClearFilters,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
  ConfigureListFilterResponse,
} from '../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import deviceHubApi from '../../utils/api/device-hub';

/**
 * Fetch all global connectors (connector ratings) from the API
 */
export const fetchAllGlobalConnectors = createAsyncThunk(
  'globalConnector/fetchAllGlobalConnectors',
  async (query: any, { dispatch, rejectWithValue }) => {
    try {
      const response = await deviceHubApi.fetchGlobalConnectorRatings(removeEmptyKeys(query));
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          message: error?.response?.data?.message || 'Failed to fetch global connectors',
          status: true,
        },
      });
      return rejectWithValue(error.response?.data);
    }
  },
);

/**
 * Refresh global connectors without toggling loading state
 */
export const refreshGlobalConnectors = createAsyncThunk(
  'globalConnector/refreshGlobalConnectors',
  async (query: any, { dispatch, rejectWithValue }) => {
    try {
      const response = await deviceHubApi.fetchGlobalConnectorRatings(removeEmptyKeys(query));
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          message: error?.response?.data?.message || 'Failed to refresh global connectors',
          status: true,
        },
      });
      return rejectWithValue(error.response?.data);
    }
  },
);

/**
 * Fetch global connector filter options
 */
export const fetchGlobalConnectorPageFilters = createAsyncThunk<CommonFilterProperties[], any>(
  'globalConnector/fetchGlobalConnectorPageFilters',
  async (query, { dispatch }) => {
    try {
      const response = await deviceHubApi.connectorRatingFilter(query);
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response?.data);
      return updatedResponse;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          message: error?.response?.data?.message || 'Something went wrong!',
          code: error?.response?.data?.code || 200,
          status: true,
        },
      });
      throw error;
    }
  },
);

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

/**
 * Initial state for Global Connector slice
 */
const initialState = {
  connectors: [] as any[],
  loading: false,
  totalResults: 0,
  totalPages: 0,
  page: 1,
  limit: 10,
  filters: availableFilters,
  isFilterUpdated: false,
  filterListLoading: false,
  sortBy: 'desc',
  sortField: 'createdAt',
};

const globalConnectorSlice = createSlice({
  name: 'globalConnector',
  initialState,
  reducers: {
    /**
     * Set current page
     * @param {any} state - The state
     * @param {any} action - The action
     */
    setGlobalConnectorPage: (state, action) => {
      state.page = action.payload;
    },
    /**
     * Set limit
     * @param {any} state - The state
     * @param {any} action - The action
     */
    setGlobalConnectorLimit: (state, action) => {
      state.limit = action.payload;
    },
    /**
     * Update filters
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    updateGlobalConnectorPageFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      }
    },
    /**
     * Disable filter applied flag
     * @param {any} state - The state
     */
    disableGlobalConnectorPageFilterApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * Set sort by
     * @param {any} state - The state
     * @param {any} action - The action
     */
    setGlobalConnectorSortBy: (state, action) => {
      state.sortBy = action.payload;
    },
    /**
     * Set sort field
     * @param {any} state - The state
     * @param {any} action - The action
     */
    setGlobalConnectorSortField: (state, action) => {
      state.sortField = action.payload;
    },
  },
  /**
   * Handle asynchronous thunk actions
   * @param {any} builder - The builder
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllGlobalConnectors.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllGlobalConnectors.fulfilled, (state, action) => {
        state.loading = false;
        state.connectors = action.payload.results || action.payload || [];
        state.totalResults =
          action.payload.totalResults ||
          (action.payload.results ? action.payload.results.length : 0);
        state.totalPages = action.payload.totalPages || 1;
        state.page = action.payload.page || state.page;
        state.limit = action.payload.limit || state.limit;
      })
      .addCase(fetchAllGlobalConnectors.rejected, (state) => {
        state.loading = false;
      })
      .addCase(refreshGlobalConnectors.fulfilled, (state, action) => {
        state.connectors = action.payload.results || action.payload || [];
        state.totalResults =
          action.payload.totalResults ||
          (action.payload.results ? action.payload.results.length : 0);
        state.totalPages = action.payload.totalPages || 1;
        state.page = action.payload.page || state.page;
        state.limit = action.payload.limit || state.limit;
      })
      .addCase(fetchGlobalConnectorPageFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchGlobalConnectorPageFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload];
        },
      )
      .addCase(fetchGlobalConnectorPageFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

export const {
  setGlobalConnectorPage,
  setGlobalConnectorLimit,
  updateGlobalConnectorPageFilters,
  disableGlobalConnectorPageFilterApplied,
  setGlobalConnectorSortBy,
  setGlobalConnectorSortField,
} = globalConnectorSlice.actions;

export default globalConnectorSlice.reducer;
